import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../api/hooks';
import type {
  AppointmentResponse,
  CreatePatientRequest,
  PatientOverviewResponse,
  PatientResponse,
  PatientSummaryResponse,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { useChartsShown } from '../components/charts/useChartsShown';
import { PageHeader } from '../components/PageHeader';
import { phone as formatPhone, shortDateSmart } from '../i18n/format';
import { ErrorState, LoadingState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';
import { isReleased } from '../config/release';
import { useLocalisedName } from '../i18n/localisedName';
import { ClinicalPanel } from './clinical/ClinicalPanel';
import { PatientsOverview } from './insight/PatientsOverview';
import { AppointmentDetails } from './scheduling/AppointmentDetails';
import { BookingModal } from './scheduling/BookingModal';
import { clinicDay, clinicTime, clinicToday } from './scheduling/clinicTime';
import { useBranchPicker } from './scheduling/useBranchPicker';

/**
 * Patient search, the ten-second create form, and the profile (FR-102, FR-405).
 *
 * The profile has a clinical tab from M7, and reception cannot see it — not because it is hidden
 * from them, but because the API refuses the call. The tab is rendered for a viewer holding
 * CLINICAL_RECORD_READ, which is a courtesy to the reader rather than the boundary itself.
 *
 * The create form asks for a name and a phone number and nothing else, because anything else is a
 * field a receptionist would invent under pressure rather than leave blank — and a wrong date of
 * birth is worse than an absent one.
 *
 * Above the search, the clinic's patients as charts (CR-33). The desk mostly comes here to find
 * somebody, so the charts fold away and stay folded for that browser.
 */
export function PatientsPage() {
  const { t, i18n } = useTranslation();
  const permissions = useAuthStore((state) => state.permissions);
  const canCreate = permissions.has('PATIENT_CREATE');
  const canBook = permissions.has('APPOINTMENT_CREATE');

  const { clinicId, branchId } = useBranchPicker();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [chartsShown, showCharts] = useChartsShown('patients');

  const results = useApiQuery<PatientSummaryResponse[]>(
    queryKeys.patients(clinicId ?? '', search),
    `/clinics/${clinicId}/patients?q=${encodeURIComponent(search)}`,
    { enabled: Boolean(clinicId) },
  );
  const overview = useApiQuery<PatientOverviewResponse>(
    queryKeys.patientOverview(clinicId ?? ''),
    `/clinics/${clinicId}/patients/overview`,
    { enabled: Boolean(clinicId) && chartsShown },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.patients')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.patients')}
        description={t('patients.description')}
        actions={
          <>
            <button type="button" className="button button--ghost" onClick={() => showCharts(!chartsShown)}>
              {chartsShown ? t('overview.hide') : t('overview.show')}
            </button>
            {canCreate ? (
              <button type="button" className="button button--primary" onClick={() => setCreating((open) => !open)}>
                {creating ? t('common.cancel') : t('patients.add')}
              </button>
            ) : null}
          </>
        }
      />

      {chartsShown ? (
        overview.data && (overview.data.total ?? 0) > 0 ? (
          <PatientsOverview overview={overview.data} />
        ) : overview.isPending ? (
          <LoadingState rows={3} />
        ) : overview.error ? (
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
        ) : null
      ) : null}

      {creating ? (
        <NewPatientForm
          clinicId={clinicId}
          onCreated={(created) => {
            setCreating(false);
            setSelected(created.id ?? null);
          }}
        />
      ) : null}

      <div className="toolbar">
        <label className="field">
          <span className="field__label">{t('patients.search')}</span>
          <input
            className="field__input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('patients.searchHint')}
          />
        </label>
      </div>

      <AsyncSection
        query={results}
        skeletonRows={6}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={search.trim().length >= 2 ? t('patients.noMatchesTitle') : t('patients.emptyTitle')}
        emptyDescription={
          search.trim().length >= 2 ? t('patients.noMatchesDescription') : t('patients.emptyDescription')
        }
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('patients.name')}</th>
                  <th scope="col">{t('patients.phone')}</th>
                  <th scope="col">{t('patients.reliability')}</th>
                  <th scope="col">{t('patients.lastSeen')}</th>
                  <th scope="col">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <th scope="row">{row.fullName}</th>
                    <td className="ltr">{formatPhone(row.phone)}</td>
                    <td>
                      {(row.noShowCount ?? 0) === 0 && (row.lateCancellationCount ?? 0) === 0 ? (
                        <span className="pill pill--good">{t('patients.reliable')}</span>
                      ) : (
                        <span className="pill pill--waiting">
                          {/* `count` as well as `noShows`: i18next picks the plural form from
                              `count` and from nothing else, and the two are the same number here —
                              the missed-appointment tally is what the sentence is counting. */}
                          {t('patients.misses', {
                            count: row.noShowCount ?? 0,
                            noShows: row.noShowCount ?? 0,
                            late: row.lateCancellationCount ?? 0,
                          })}
                        </span>
                      )}
                    </td>
                    <td>{shortDateSmart(row.lastSeenOn, i18n.language)}</td>
                    <td>
                      <div className="table__actions">
                        <button
                          type="button"
                          className="button button--soft button--compact"
                          onClick={() => setSelected(row.id ?? null)}
                        >
                          {t('patients.open')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {selected ? (
        <PatientProfile
          clinicId={clinicId}
          branchId={branchId}
          membershipId={selected}
          canBook={canBook}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </>
  );
}

function NewPatientForm({
  clinicId,
  onCreated,
}: {
  readonly clinicId: string;
  readonly onCreated: (patient: PatientResponse) => void;
}) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');

  // FR-102's soft duplicate warning. Looked up as the number is typed, so the picker is on screen
  // before the button is pressed rather than as a refusal afterwards — a family sharing one number
  // is normal (DEC-016) and must stay possible.
  const duplicates = useApiQuery<PatientSummaryResponse[]>(
    queryKeys.patientsByPhone(clinicId, phone),
    `/clinics/${clinicId}/patients/by-phone?phone=${encodeURIComponent(phone)}`,
    { enabled: phone.replace(/\D/g, '').length >= 10 },
  );

  const create = useApiMutation<PatientResponse, CreatePatientRequest>(
    (body) => ({ path: `/clinics/${clinicId}/patients`, options: { method: 'POST', body } }),
    { invalidates: [['clinics', clinicId, 'patients']] },
  );

  return (
    <form
      className="panel"
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate({ fullName, phone }, { onSuccess: onCreated });
      }}
    >
      <div className="form-grid">
        <label className="field">
          <span className="field__label">{t('patients.name')}</span>
          <input
            className="field__input"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            required
          />
        </label>
        <label className="field">
          <span className="field__label">{t('patients.phone')}</span>
          <input
            className="field__input ltr"
            inputMode="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="01001234567"
            required
          />
        </label>
      </div>

      {(duplicates.data ?? []).length > 0 ? (
        <div className="callout">
          <p>{t('patients.duplicateWarning', { count: (duplicates.data ?? []).length })}</p>
          <ul>
            {(duplicates.data ?? []).map((row) => (
              <li key={row.id}>
                {row.fullName} · <span className="ltr">{row.phone}</span>
              </li>
            ))}
          </ul>
          <p className="field__hint">{t('patients.duplicateHint')}</p>
        </div>
      ) : null}

      {create.error ? <ErrorState error={create.error} /> : null}

      <div className="table__actions">
        <button type="submit" className="button button--primary" disabled={create.isPending}>
          {t('patients.save')}
        </button>
      </div>
    </form>
  );
}

function PatientProfile({
  clinicId,
  branchId,
  membershipId,
  canBook,
  onClose,
}: {
  readonly clinicId: string;
  readonly branchId: string | null;
  readonly membershipId: string;
  readonly canBook: boolean;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const localised = useLocalisedName();
  const permissions = useAuthStore((state) => state.permissions);
  // The tab also waits for the clinical stage of the staged release (config/release.ts).
  const canReadClinical = permissions.has('CLINICAL_RECORD_READ') && isReleased('patients.clinical');
  const [booking, setBooking] = useState(false);
  const [tab, setTab] = useState<'info' | 'clinical'>('info');
  const [opened, setOpened] = useState<AppointmentResponse | null>(null);

  const patient = useApiQuery<PatientResponse>(
    queryKeys.patient(clinicId, membershipId),
    `/clinics/${clinicId}/patients/${membershipId}`,
  );

  const appointments = useApiQuery<AppointmentResponse[]>(
    queryKeys.patientAppointments(clinicId, membershipId),
    `/clinics/${clinicId}/patients/${membershipId}/appointments`,
  );

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('patients.profile')}>
      <div className="modal__panel">
        <header className="modal__header">
          <h2>{patient.data?.fullName ?? t('patients.profile')}</h2>
          <button type="button" className="button button--ghost button--compact" onClick={onClose}>
            {t('common.close')}
          </button>
        </header>

        <div className="modal__body">
          {canReadClinical ? (
            <div className="tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'info'}
                className={tab === 'info' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
                onClick={() => setTab('info')}
              >
                {t('patients.info')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'clinical'}
                className={tab === 'clinical' ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
                onClick={() => setTab('clinical')}
              >
                {t('clinical.tab')}
              </button>
            </div>
          ) : null}

          {tab === 'clinical' && canReadClinical ? (
            <ClinicalPanel
              clinicId={clinicId}
              membershipId={membershipId}
              patientName={patient.data?.fullName ?? ''}
            />
          ) : (
          <>
          <section className="section">
            <h3 className="section__title">{t('patients.info')}</h3>
            <dl className="detail-list">
              <div>
                <dt>{t('patients.phone')}</dt>
                <dd className="ltr">{patient.data?.phone ?? '—'}</dd>
              </div>
              <div>
                <dt>{t('patients.source')}</dt>
                <dd>{patient.data?.source ? t(`patients.sources.${patient.data.source}`) : '—'}</dd>
              </div>
              <div>
                <dt>{t('patients.reliability')}</dt>
                <dd>
                  {t('patients.misses', {
                    noShows: patient.data?.noShowCount ?? 0,
                    late: patient.data?.lateCancellationCount ?? 0,
                  })}
                </dd>
              </div>
              <div>
                <dt>{t('patients.note')}</dt>
                <dd>{patient.data?.note ?? '—'}</dd>
              </div>
            </dl>
            {/* FR-405: the clinical record is a different module behind a permission reception does
                not hold, and this tab is not rendered for them. */}
            {canReadClinical ? null : <p className="field__hint">{t('patients.noClinicalHere')}</p>}
          </section>

          <section className="section">
            <h3 className="section__title">{t('patients.appointments')}</h3>
            {(appointments.data ?? []).length === 0 ? (
              <p className="field__hint">{t('patients.noAppointments')}</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('board.date')}</th>
                      <th scope="col">{t('booking.service')}</th>
                      <th scope="col">{t('booking.doctor')}</th>
                      <th scope="col">{t('common.status')}</th>
                      <th scope="col">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(appointments.data ?? []).map((appointment) => (
                      <tr key={appointment.id}>
                        <td className="ltr">
                          {appointment.startsAt ? clinicDay(appointment.startsAt) : ''} {clinicTime(appointment.startsAt)}
                        </td>
                        <td>
                          {localised.name({
                            nameEn: appointment.serviceNameEn,
                            nameAr: appointment.serviceNameAr,
                          })}
                        </td>
                        <td>{appointment.doctorName ?? '—'}</td>
                        <td>{t(`board.status.${appointment.status ?? 'BOOKED'}`)}</td>
                        <td>
                          <button
                            type="button"
                            className="button button--ghost button--compact"
                            onClick={() => setOpened(appointment)}
                          >
                            {t('appointment.details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          </>
          )}
        </div>

        <footer className="modal__footer">
          {canBook && branchId ? (
            <button type="button" className="button button--primary" onClick={() => setBooking(true)}>
              {t('board.book')}
            </button>
          ) : null}
        </footer>
      </div>

      {opened ? (
        <AppointmentDetails clinicId={clinicId} appointment={opened} onClose={() => setOpened(null)} />
      ) : null}

      {booking && branchId && patient.data ? (
        <BookingModal
          clinicId={clinicId}
          branchId={branchId}
          on={clinicToday()}
          patient={{
            id: patient.data.id,
            fullName: patient.data.fullName,
            phone: patient.data.phone,
            noShowCount: patient.data.noShowCount,
            lateCancellationCount: patient.data.lateCancellationCount,
          }}
          onClose={() => setBooking(false)}
          onBooked={() => setBooking(false)}
        />
      ) : null}
    </div>
  );
}
